import React, { useCallback } from 'react';

import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  createStyles,
  WithStyles,
  withStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import locales from '../../locales';
import { Routers } from '../../config/constants';
import { ReactComponent as HomeIcon } from '../Common/Icons/newDesign/home.svg';
import { ReactComponent as BookingIcon } from '../Common/Icons/newDesign/notepad.svg';
import { ReactComponent as SchemaIcon } from '../Common/Icons/newDesign/scheme.svg';
import { ReactComponent as StatisticIcon } from '../Common/Icons/newDesign/chart.svg';
import { ReactComponent as IntegrationsIcon } from '../Common/Icons/newDesign/integration.svg';
import { ReactComponent as GuestAcquisitionIcon } from '../Common/Icons/newDesign/guest.svg';
import { ReactComponent as CardIcon } from '../Common/Icons/newDesign/settings.svg';
import SideBarItem from './SideBarItem';
import { NewVersionBanner } from '../../_FSD/widgets/newVersionBanner';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { Employees } from '../../entities';
import LoyalityIcon from "../Common/Icons/LoyalityIcon";
import Button from "@material-ui/core/Button";

const styles = (theme: Theme) => createStyles({
  menu: {
    padding: '22px 0 0 8px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
    color: '#323232',
    backgroundColor: '#F0F1F4',
    position: 'relative',
    // Минималистичный скроллбар для Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
    // Минималистичный скроллбар для Chrome/Safari
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '2px',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
      },
    },
    [theme.breakpoints.only('xs')]: {
      padding: '16px 0 0 8px',
    },
    [theme.breakpoints.only('sm')]: {
      padding: '18px 0 0 8px',
    },
  },
  userMenuSection: {
    marginTop: 'auto',
    paddingBottom: '16px',
  },
  logoutItem: {
    height: 44,
    padding: 0,
    backgroundColor: '#F0F1F4',
    position: 'relative',
    '&:hover': {
      borderRadius: '10px 0 0 10px',
      backgroundColor: 'rgba(76, 104, 147, 0.12)',
    },
  },
  listItemContainer: {
    display: 'flex',
    padding: 12,
    width: '100%',
    height: '100%',
    zIndex: 2,
    [theme.breakpoints.only('xs')]: {
      padding: 10,
    },
    [theme.breakpoints.only('sm')]: {
      padding: 11,
    },
  },
  textWrapper: {
    marginTop: 0,
    marginBottom: 0,
  },
  text: {
    color: '#323232',
    fontSize: '16px',
    lineHeight: '20px',
  },
  menuHeader: {
    paddingTop: '22px',
    paddingBottom: '15px',
    marginBottom: '10px',
    [theme.breakpoints.only('xs')]: {
      paddingTop: '16px',
    },
    [theme.breakpoints.only('sm')]: {
      paddingTop: '18px',
    },
  },
  menuIcon: {
    fontSize: 32,
    color: '#323232',
    width: '20px',
    height: '20px',
  },
  newReserveWrap: {
    width: 'calc(100% - 8px)',
    height: 52,
    marginTop: 20,
    marginBottom: 20,
    [theme.breakpoints.only('xs')]: {
      height: 44,
    },
    [theme.breakpoints.only('sm')]: {
      height: 48,
    },
  },
  emptyBlock: {
    height: '10px',
    backgroundColor: '#F0F1F4',
  }
});

interface IProps extends WithStyles<typeof styles> {
  pathname: string;
  handleSwitchDrawerClose: () => void;
  isRegistrationCompleted: boolean;
  handleOpenReserveCreateModal?: () => void;
  onLogout?: () => void;
}


/** @description Компонент сайдбара */
const SideBar: React.FC<IProps> = ({
  classes,
  pathname,
  handleSwitchDrawerClose,
  handleOpenReserveCreateModal,
  isRegistrationCompleted,
  onLogout,
}) => {
  /** @description Стейт видимости сайдбара */
  // const [showSubMenuSettings, setShowSubMenuSettings] = useState(false);
  // const [showSubMenuIntegrations, setShowSubMenuIntegrations] = useState(false);

  /**
   * @description Функция проверки адреса подменю на принадлежность разделу "Ресторан"
   * @param pathname
   * @returns {boolean}
   */
  const checkIsRestaurantItem = useCallback((pathname: string) => {
    const restaurantSubMenuRouters = {
      [Routers.reservesSystem]: 'card',
      [Routers.restaurantScheme]: 'card',
      [Routers.cardMenu]: 'card',
      [Routers.cardAction]: 'card',
      [Routers.cardSettings]: 'card',
      [Routers.employees]: 'card',
      [Routers.prepay]: 'card',
      [Routers.about]: 'card',
    };

    return restaurantSubMenuRouters[pathname] === 'card';
  }, []);

  // /**
  //  * @description Ручка для открытия/закрытия подменю
  //  */
  // const handleSwitchSubMenuSettings = useCallback((event: MouseEvent<HTMLButtonElement>) => {
  //   event.stopPropagation();
  //   event.preventDefault();
  //   setShowSubMenuSettings(!showSubMenuSettings);
  // }, [showSubMenuSettings]);
  //
  // const handleSwitchSubMenuIntegrations = useCallback((event: MouseEvent<HTMLButtonElement>) => {
  //   event.stopPropagation();
  //   event.preventDefault();
  //   setShowSubMenuIntegrations(!showSubMenuIntegrations);
  // }, [showSubMenuIntegrations]);

  const { data: e } = Employees.useCurrentEmployee();
  const settingItems: Array<{ name: string, route: string }> = [];
  const integrationsItems = [
    {
      name: locales.t('gateways.title'),
      route: Routers.gateways,
    }
    /*{
      name: locales.t('telephony.title'),
      route: Routers.telephony,
    }*/
  ]
  const isAdministrator = Boolean(e?.authorities?.indexOf("ADMINISTRATOR") > -1);
  const hasStatsAccess = Employees.hasAccess(e, 'stats:access');
  const hasReservesAccess = Employees.hasAccess(e, 'reserves:access');
  const hasIntegrationsAccess = Employees.hasAccess(e, 'integrations:access');
  const hasSettingsAccess = Employees.hasAccess(e, 'restaurantSettings:access');
  const hasEmployeesAccess = Employees.hasAccess(e, 'employees:access');

  if (hasSettingsAccess) {
    settingItems.push({
      name: locales.t('aboutRestaurant.title'),
      route: Routers.about,
    });

    settingItems.push({
      name: locales.t('reservesSystem.title'),
      route: Routers.reservesSystem,
    });

    settingItems.push({
      name: locales.t('prepay.title'),
      route: Routers.prepay,
    });

    settingItems.push({
      name: locales.t('restaurantSchema.title'),
      route: Routers.restaurantScheme,
    });
  }

  if (hasEmployeesAccess) {
    settingItems.push({
      name: locales.t('employees.title'),
      route: Routers.employees,
    });
  }

  // settingItems.push({
  //   name: locales.t('common.profile'),
  //   route: Routers.profile,
  // });

  return (
    <List className={classes.menu}>
      <div className={classes.emptyBlock} />
      {(!isRegistrationCompleted || hasStatsAccess) && (
        <SideBarItem
          route={Routers.home}
          pathname={pathname}
          handleSwitchDrawerClose={handleSwitchDrawerClose}
          text="Главная"
          icon={<HomeIcon className={classes.menuIcon} />}
          isActive={pathname === Routers.home}
        />
      )}

      {(!isRegistrationCompleted || hasReservesAccess) && (
        <SideBarItem
          disabled={!isRegistrationCompleted}
          route={Routers.booking}
          pathname={pathname}
          handleSwitchDrawerClose={handleSwitchDrawerClose}
          text="Резервы"
          icon={<BookingIcon className={classes.menuIcon} />}
          isActive={pathname === Routers.booking}

        />
      )}

      {(!isRegistrationCompleted || hasReservesAccess) && (
        <SideBarItem
          disabled={!isRegistrationCompleted}
          route={Routers.reservesSchemas}
          pathname={pathname}
          handleSwitchDrawerClose={handleSwitchDrawerClose}
          text="Схема зала"
          icon={<SchemaIcon className={classes.menuIcon} />}
          isActive={pathname === Routers.reservesSchemas}
        />
      )}

      {(!isRegistrationCompleted || hasStatsAccess) && (
        <SideBarItem
          disabled={!isRegistrationCompleted}
          route={Routers.statistics}
          pathname={pathname}
          handleSwitchDrawerClose={handleSwitchDrawerClose}
          text="Статистика"
          icon={<StatisticIcon className={classes.menuIcon} />}
          isActive={pathname === Routers.statistics}
        />
      )}

      {(!isRegistrationCompleted || hasIntegrationsAccess) && (
        <SideBarItem
          disabled={!isRegistrationCompleted}
          route={integrationsItems[0].route}
          // showSubMenu={showSubMenuIntegrations}
          pathname={pathname}
          // handleSwitchSubMenu={handleSwitchSubMenuIntegrations}
          handleSwitchDrawerClose={handleSwitchDrawerClose}
          text="Интеграции"
          icon={<IntegrationsIcon className={classes.menuIcon} />}
          isActive={pathname === integrationsItems[0].route}
          // subMenu={integrationsItems}
        />
      )}

      {(!isRegistrationCompleted || hasIntegrationsAccess) && (
        <SideBarItem
          disabled={!isRegistrationCompleted}
          route={Routers.guestsAcquisition}
          pathname={pathname}
          handleSwitchDrawerClose={handleSwitchDrawerClose}
          text="Привлечение гостей"
          icon={<GuestAcquisitionIcon className={classes.menuIcon} />}
          isActive={pathname === Routers.guestsAcquisition}
        />
      )}
      {(!isRegistrationCompleted || isAdministrator) && (
        <SideBarItem
          disabled={!isRegistrationCompleted}
          route={Routers.loyalty}
          pathname={pathname}
          handleSwitchDrawerClose={handleSwitchDrawerClose}
          text="Лояльность"
          icon={<LoyalityIcon className={classes.menuIcon} />}
          isActive={pathname === Routers.loyalty}
        />
      )}

      {(!isRegistrationCompleted || settingItems.length > 0) && (
        <SideBarItem
          // Пусть пункт всегда будет доступен, чтобы можно было удалить ресторан, даже
          //   если подключение еще было не полностью закончено
          // disabled={!isRegistrationCompleted}
          // route={Routers.reservesSystem}
          disabled={!e}
          route={settingItems.length > 0 ? settingItems[0].route : Routers.settings}
          pathname={pathname}
          // showSubMenu={showSubMenuSettings}
          // handleSwitchSubMenu={handleSwitchSubMenuSettings}
          handleSwitchDrawerClose={handleSwitchDrawerClose}
          text="Настройки"
          icon={<CardIcon className={classes.menuIcon} />}
          // subMenu={e ? settingItems : undefined}
          isActive={checkIsRestaurantItem(pathname)}
        />
      )}
      <div className={classes.emptyBlock} />
      {(!isRegistrationCompleted || hasReservesAccess)
        && handleOpenReserveCreateModal && (
          <Button
            disabled={!isRegistrationCompleted}
            className={classes.newReserveWrap}
            variant="contained"
            color="primary"
            onClick={() => {
              handleOpenReserveCreateModal();
              handleSwitchDrawerClose();
            }}
          >
            Новый резерв
          </Button>
        )}

      {onLogout && (
        <div className={classes.userMenuSection}>
          <ListItem
            className={classes.logoutItem}
            onClick={() => {
              onLogout();
              handleSwitchDrawerClose();
            }}
            button
          >
            <div className={classes.listItemContainer}>
              <ListItemIcon style={{ minWidth: 20, paddingRight: 10 }}>
                <ExitToAppIcon className={classes.menuIcon} />
              </ListItemIcon>
              <ListItemText className={classes.textWrapper} primary={(
                <Typography
                  variant="body1"
                  className={classes.text}
                >
                  {locales.t('common.logOut')}
                </Typography>
              )}
              />
            </div>
          </ListItem>
        </div>
      )}
      <NewVersionBanner />
    </List>
  );
};

export default withStyles(styles)(SideBar);
